import React, { useEffect, useState } from "react";
import "./App.css";
import { projects, staff as defaultStaff } from "./data";

// Normal tile types and corresponding heroes by index:
const tileTypes = ["⚔️", "⛏️", "💰", "🧝🏻", "🧙"];
const tileColors = {
  "⚔️": "red",
  "⛏️": "green",
  "💰": "purple",
  "🧝🏻": "darkblue",
  "🧙": "yellow",
};
const boardSize = 6;

// Return a random normal tile
function getRandomTile() {
  return tileTypes[Math.floor(Math.random() * tileTypes.length)];
}

// Generate an initial board (2D array of tiles)
function generateBoard() {
  let board;
  do {
    board = Array.from({ length: boardSize }, () =>
      Array.from({ length: boardSize }, () => getRandomTile())
    );
  } while (checkMatches(board).hadMatches);
  return board;
}

/* 
  checkMatches scans the board for contiguous horizontal and vertical sequences.
  Instead of clearing them immediately, it returns an array of positions to animate.
*/
function checkMatches(board) {
  // We work directly with the provided board.
  let hadMatches = false;
  const scoreDelta = Array(tileTypes.length).fill(0);
  const toClear = Array.from({ length: boardSize }, () =>
    Array(boardSize).fill(false)
  );

  // --- Horizontal Matches ---
  for (let i = 0; i < boardSize; i++) {
    let j = 0;
    while (j < boardSize) {
      const tile = board[i][j];
      if (!tile || !tileTypes.includes(tile)) {
        j++;
        continue;
      }
      const start = j;
      while (j < boardSize && board[i][j] === tile) {
        j++;
      }
      const count = j - start;
      if (count >= 3) {
        hadMatches = true;
        const heroIndex = tileTypes.indexOf(tile);
        scoreDelta[heroIndex] += count * 10;
        for (let k = start; k < j; k++) {
          toClear[i][k] = true;
        }
      }
    }
  }

  // --- Vertical Matches ---
  for (let j = 0; j < boardSize; j++) {
    let i = 0;
    while (i < boardSize) {
      const tile = board[i][j];
      if (!tile || !tileTypes.includes(tile)) {
        i++;
        continue;
      }
      const start = i;
      while (i < boardSize && board[i][j] === tile) {
        i++;
      }
      const count = i - start;
      if (count >= 3) {
        hadMatches = true;
        const heroIndex = tileTypes.indexOf(tile);
        scoreDelta[heroIndex] += count * 10;
        for (let k = start; k < i; k++) {
          toClear[k][j] = true;
        }
      }
    }
  }

  // Record positions of matched (to-be-cleared) tiles.
  const matchedPositions = [];
  for (let i = 0; i < boardSize; i++) {
    for (let j = 0; j < boardSize; j++) {
      if (toClear[i][j]) {
        matchedPositions.push({ i, j });
      }
    }
  }

  return { hadMatches, scoreDelta, matchedPositions };
}

/* 
  applyGravity implements upward gravity. For each column:
    - It collects all non-null tiles (from top to bottom).
    - Then it appends new random tiles at the bottom until the column is full.
*/
function applyGravity(board) {
  let newBoard = board.map((row) => row.slice());
  for (let j = 0; j < boardSize; j++) {
    const column = [];
    for (let i = 0; i < boardSize; i++) {
      if (newBoard[i][j] !== null) {
        column.push(newBoard[i][j]);
      }
    }
    while (column.length < boardSize) {
      column.push(getRandomTile());
    }
    for (let i = 0; i < boardSize; i++) {
      newBoard[i][j] = column[i];
    }
  }
  return newBoard;
}

function shuffleBoard(board) {
  const flat = board.flat();
  let newBoard;
  do {
    const shuffled = [...flat];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    newBoard = [];
    for (let i = 0; i < boardSize; i++) {
      newBoard.push(shuffled.slice(i * boardSize, (i + 1) * boardSize));
    }
  } while (checkMatches(newBoard).hadMatches);
  return newBoard;
}

const App = ({
  heroes = defaultStaff.slice(0, 5),
  heroLife: initialHeroLife,
  enemyLife: initialEnemyLife = 5000,
  bgColor = "#222",
  onRestartSetup = () => {},
}) => {
  const defaultHeroLife = heroes.map((h) => h.life);
  if (!initialHeroLife) {
    initialHeroLife = defaultHeroLife;
  }
  const project =
    projects.find((p) => p.life === initialEnemyLife) || projects[0];
  const [board, setBoard] = useState(generateBoard());
  const [selectedTile, setSelectedTile] = useState(null);
  // matchedPositions holds coordinates of tiles that are matching (to animate them)
  const [matchedPositions, setMatchedPositions] = useState([]);

  // Hero scores and other stats
  const [heroScores, setHeroScores] = useState(Array(heroes.length).fill(0));
  const maxScore = 100;
  const [heroLife, setHeroLife] = useState(initialHeroLife);
  const [heroDamage, setHeroDamage] = useState([...initialHeroLife]);
  const [enemyLife, setEnemyLife] = useState(initialEnemyLife);
  const [enemyMana, setEnemyMana] = useState(0);
  const [enemyCounter, setEnemyCounter] = useState(3);
  const [counterTarget, setCounterTarget] = useState(null);
  const [gameOver, setGameOver] = useState(false);

  // Drag & Drop Handlers
  const handleDragStart = (e, rowIndex, colIndex) => {
    setSelectedTile({ rowIndex, colIndex });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, rowIndex, colIndex) => {
    e.preventDefault();
    if (selectedTile) {
      const { rowIndex: sRow, colIndex: sCol } = selectedTile;
      // Check if adjacent (Manhattan distance of 1)
      if (Math.abs(rowIndex - sRow) + Math.abs(colIndex - sCol) === 1) {
        swapTiles(sRow, sCol, rowIndex, colIndex);
      }
    }
    setSelectedTile(null);
  };

  const handleDragEnd = () => {
    setSelectedTile(null);
  };

  const restartGame = () => {
    setBoard(generateBoard());
    setHeroScores([0, 0, 0, 0, 0]);
    setHeroDamage([...heroLife]);
    setEnemyLife(initialEnemyLife);
    setEnemyMana(0);
    setEnemyCounter(3);
    setCounterTarget(null);
    setGameOver(false);
  };

  // Also support click swapping if desired:
  const handleTileClick = (rowIndex, colIndex) => {
    if (!selectedTile) {
      setSelectedTile({ rowIndex, colIndex });
    } else {
      const { rowIndex: sRow, colIndex: sCol } = selectedTile;
      if (Math.abs(rowIndex - sRow) + Math.abs(colIndex - sCol) === 1) {
        swapTiles(sRow, sCol, rowIndex, colIndex);
      }
      setSelectedTile(null);
    }
  };

  // Process Board Matches with Animation
  const processBoardAsync = async (currentBoard, addScore) => {
    let boardState = currentBoard;
    let totalScoreDelta = Array(tileTypes.length).fill(0);
    while (true) {
      const { hadMatches, scoreDelta, matchedPositions } =
        checkMatches(boardState);
      if (!hadMatches) break;

      // Accumulate score delta.
      totalScoreDelta = totalScoreDelta.map((s, idx) => s + scoreDelta[idx]);

      // Set matched positions for animation.
      setMatchedPositions(matchedPositions);
      const tilesMatched = matchedPositions.length;
      if (tilesMatched > 0) {
        setEnemyLife((life) => Math.max(life - tilesMatched * 10, 0));
        setEnemyMana((mana) => Math.min(mana + tilesMatched * 5, 100));
      }
      // Wait for animation to complete (500ms here)
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Clear the matched cells.
      let boardAfterClearing = boardState.map((row) => row.slice());
      matchedPositions.forEach(({ i, j }) => {
        boardAfterClearing[i][j] = null;
      });
      // Clear the animation state.
      setMatchedPositions([]);
      // Apply gravity.
      boardState = applyGravity(boardAfterClearing);
      setBoard(boardState);
    }
    addScore(totalScoreDelta);
    return boardState;
  };

  // Swap Tiles and Process Matches
  const swapTiles = async (row1, col1, row2, col2) => {
    // Preserve current board to revert if no match is created
    const originalBoard = board.map((row) => [...row]);
    // Create a deep copy for the tentative swap
    const newBoard = board.map((row) => [...row]);
    // Swap the two tiles.
    [newBoard[row1][col1], newBoard[row2][col2]] = [
      newBoard[row2][col2],
      newBoard[row1][col1],
    ];
    setBoard(newBoard);

    // Add swapping animation class
    setTimeout(() => {
      document
        .querySelectorAll(".tile")
        .forEach((tile) => tile.classList.remove("swapping"));
    }, 300);

    const { hadMatches } = checkMatches(newBoard);

    if (!hadMatches) {
      // Revert the swap after a short delay to show the move
      await new Promise((resolve) => setTimeout(resolve, 300));
      setBoard(originalBoard);
      return;
    }

    // Process board for matches (with animations)
    const processedBoard = await processBoardAsync(newBoard, (scoreDelta) => {
      setHeroScores((prevScores) =>
        prevScores.map((score, idx) => score + scoreDelta[idx])
      );
    });

    setBoard(processedBoard);
    setEnemyCounter((c) => {
      const next = c - 1;
      if (next <= 0) {
        const livingIndexes = heroDamage
          .map((hp, idx) => (hp > 0 ? idx : null))
          .filter((idx) => idx !== null);
        if (livingIndexes.length > 0) {
          const randIndex =
            livingIndexes[Math.floor(Math.random() * livingIndexes.length)];
          setCounterTarget(randIndex);
          setHeroDamage((dmg) => {
            const updated = [...dmg];
            updated[randIndex] = Math.max(updated[randIndex] - 50, 0);
            return updated;
          });
        }
        return 3;
      }
      return next;
    });
  };

  // Hero click handlers (for when a hero's progress bar is full)
  // Developers Heroes will reduce the Enemy's life
  const handleClickDev = () => {
    if (heroScores[0] >= maxScore) {
      const aliveHeroes = heroDamage.filter((life) => life > 0).length;
      setEnemyLife((life) => Math.max(life - aliveHeroes * 100, 0));
      setHeroScores([
        0,
        heroScores[1],
        heroScores[2],
        heroScores[3],
        heroScores[4],
      ]);
    }
  };
  // DevOps Heroes will give mana to all heroes
  const handleClickOps = () => {
    if (heroScores[1] >= maxScore) {
      setHeroScores([
        heroScores[0] + 10,
        10,
        heroScores[2] + 10,
        heroScores[3] + 10,
        heroScores[4] + 10,
      ]);
    }
  };
  // Finance Heroes will heal all heroes life
  const handleClickFin = () => {
    if (heroScores[2] >= maxScore) {
      setHeroScores([
        heroScores[0],
        heroScores[1],
        0,
        heroScores[3],
        heroScores[4],
      ]);
      setHeroDamage((dmg) =>
        dmg.map((d, idx) => Math.min(d + 100, heroLife[idx]))
      );
    }
  };
  // UX/UI Heroes will reorder the board
  const handleClickUx = () => {
    if (heroScores[3] >= maxScore) {
      setHeroScores([
        heroScores[0],
        heroScores[1],
        heroScores[2],
        0,
        heroScores[4],
      ]);
      setBoard((prev) => shuffleBoard(prev));
    }
  };
  // PMs/TMs Heroes will delay the Enemy's attack
  const handleClickPm = () => {
    if (heroScores[4] >= maxScore) {
      setHeroScores([
        heroScores[0],
        heroScores[1],
        heroScores[2],
        heroScores[3],
        0,
      ]);
      setEnemyCounter((c) => c + 2);
    }
  };

  useEffect(() => {
    console.log(heroScores);
  }, [heroScores]);

  useEffect(() => {
    if (matchedPositions.length === 0) return;
    const enemyEl = document.querySelector(".enemy");
    if (!enemyEl) return;

    matchedPositions.forEach(({ i, j }) => {
      const tileEl = document.querySelector(
        `.tile[data-row='${i}'][data-col='${j}']`
      );
      if (!tileEl) return;
      const tileRect = tileEl.getBoundingClientRect();
      const enemyRect = enemyEl.getBoundingClientRect();
      const orb = document.createElement("div");
      orb.className = "orb";
      orb.style.backgroundColor = tileColors[board[i][j]];
      orb.style.boxShadow = `0px 0px 10px 10px ${tileColors[board[i][j]]}`;
      orb.style.left = `${tileRect.left + tileRect.width / 2 - 5}px`;
      orb.style.top = `${tileRect.top + tileRect.height / 2 - 5}px`;
      orb.style.opacity = "0";
      document.body.appendChild(orb);

      const deltaX =
        enemyRect.left +
        enemyRect.width / 2 -
        (tileRect.left + tileRect.width / 2);
      const deltaY =
        enemyRect.top +
        enemyRect.height / 2 -
        (tileRect.top + tileRect.height / 2);

      requestAnimationFrame(() => {
        orb.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
        orb.style.opacity = "1";
      });

      const removeOrb = () => {
        orb.classList.add("fadingOut");
        orb.addEventListener(
          "transitionend",
          () => {
            orb.remove();
          },
          { once: true }
        );
      };
      orb.addEventListener("transitionend", removeOrb, { once: true });
    });
  }, [matchedPositions, board]);

  useEffect(() => {
    if (enemyMana < 100) return;
    const enemyEl = document.querySelector(".enemy");
    const heroEls = document.querySelectorAll(".hero");
    if (enemyEl) {
      enemyEl.classList.add("attack");
      setTimeout(() => enemyEl.classList.remove("attack"), 500);
    }
    const enemyRect = enemyEl?.getBoundingClientRect();

    if (enemyRect) {
      heroEls.forEach((heroEl, idx) => {
        if (heroDamage[idx] <= 0) return;
        const heroRect = heroEl.getBoundingClientRect();
        const orb = document.createElement("div");
        orb.className = "orb";
        orb.style.backgroundColor =
          getComputedStyle(heroEl).borderColor || "white";
        orb.style.left = `${enemyRect.left + enemyRect.width / 2 - 5}px`;
        orb.style.top = `${enemyRect.top + enemyRect.height / 2 - 5}px`;
        orb.style.opacity = "0";
        document.body.appendChild(orb);

        const deltaX =
          heroRect.left +
          heroRect.width / 2 -
          (enemyRect.left + enemyRect.width / 2);
        const deltaY =
          heroRect.top +
          heroRect.height / 2 -
          (enemyRect.top + enemyRect.height / 2);

        requestAnimationFrame(() => {
          orb.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
          orb.style.opacity = "1";
        });

        const removeOrb = () => {
          orb.classList.add("fadingOut");
          orb.addEventListener(
            "transitionend",
            () => {
              orb.remove();
            },
            { once: true }
          );
        };
        orb.addEventListener("transitionend", removeOrb, { once: true });
      });
    }
    setHeroDamage((dmg) => dmg.map((d) => (d > 0 ? Math.max(d - 50, 0) : 0)));
    setEnemyMana(0);
  }, [enemyMana]);

  useEffect(() => {
    if (counterTarget === null) return;
    const enemyEl = document.querySelector(".enemy");
    const heroEl = document.querySelectorAll(".hero")[counterTarget];
    if (!enemyEl || !heroEl || heroDamage[counterTarget] <= 0) {
      setCounterTarget(null);
      return;
    }

    const enemyRect = enemyEl.getBoundingClientRect();
    const heroRect = heroEl.getBoundingClientRect();
    const orb = document.createElement("div");
    orb.className = "orb";
    orb.style.backgroundColor = getComputedStyle(heroEl).borderColor || "white";
    orb.style.left = `${enemyRect.left + enemyRect.width / 2 - 5}px`;
    orb.style.top = `${enemyRect.top + enemyRect.height / 2 - 5}px`;
    orb.style.opacity = "0";
    document.body.appendChild(orb);

    const deltaX =
      heroRect.left +
      heroRect.width / 2 -
      (enemyRect.left + enemyRect.width / 2);
    const deltaY =
      heroRect.top +
      heroRect.height / 2 -
      (enemyRect.top + enemyRect.height / 2);

    requestAnimationFrame(() => {
      orb.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
      orb.style.opacity = "1";
    });

    const removeOrb = () => {
      orb.classList.add("fadingOut");
      orb.addEventListener(
        "transitionend",
        () => {
          orb.remove();
        },
        { once: true }
      );
    };
    orb.addEventListener("transitionend", removeOrb, { once: true });

    setCounterTarget(null);
  }, [counterTarget]);

  useEffect(() => {
    if (enemyLife <= 0 || heroDamage.every((hp) => hp === 0)) {
      setGameOver(true);
    }
  }, [enemyLife, heroDamage]);

  return (
    <div className="game-container">
      {/* Enemies */}
      <div className="enemies">
        <div className="enemy" style={{ "--project-color": project.color }}>
          <div>
            <div>{project.fullName}</div>
            <img
              className="profile"
              src={project.path}
              alt={project.fullName}
            />
          </div>
          <progress
            className="enemyLife"
            value={enemyLife}
            max={initialEnemyLife}
            title={`${enemyLife} / ${initialEnemyLife}`}
          />
          <progress className="enemyMana" value={enemyMana} max={100} />
          <div className="enemy-counter">{enemyCounter}</div>
        </div>
      </div>

      {/* Match‑3 Board */}
      <div className="board">
        {board.map((row, rowIndex) => (
          <div key={rowIndex} className="row">
            {row.map((tile, colIndex) => {
              // Check if this cell is flagged as matched.
              const isMatched = matchedPositions.some(
                (pos) => pos.i === rowIndex && pos.j === colIndex
              );
              return (
                <div
                  key={colIndex}
                  data-row={rowIndex}
                  data-col={colIndex}
                  className={`tile
                    ${
                      selectedTile?.rowIndex === rowIndex &&
                      selectedTile?.colIndex === colIndex
                        ? "selected"
                        : ""
                    }
                    ${isMatched ? "matched" : ""}`}
                  style={{
                    backgroundColor: tileColors[tile],
                    transition: "transform 0.3s ease-in-out",
                  }}
                  draggable="true"
                  onClick={() => handleTileClick(rowIndex, colIndex)}
                  onDragStart={(e) => handleDragStart(e, rowIndex, colIndex)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, rowIndex, colIndex)}
                  onDragEnd={handleDragEnd}
                >
                  {tile}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Heroes (displaying updated scores) */}
      <div className="heroes">
        <div>
          <div
            className="hero"
            style={{
              whiteSpace: "pre-line",
              borderColor: heroes[0].color,
              "--hero-color": heroes[0].color,
            }}
          >
            <div>
              <div className="fullName">{heroes[0].fullName}</div>
              <img
                className="profile"
                src={heroes[0].path}
                alt={heroes[0].fullName}
              />
            </div>
            {heroDamage[0] <= 0 ? (
              <div className="dead-label">FIRED</div>
            ) : (
              <>
                <progress
                  className="heroLife"
                  value={heroDamage[0]}
                  max={heroLife[0]}
                  title={`${heroDamage[0]} / ${heroLife[0]}`}
                />
                <progress
                  className={`heroMana ${
                    heroScores[0] >= maxScore ? "clickable" : ""
                  }`}
                  value={heroScores[0]}
                  max={maxScore}
                  onClick={handleClickDev}
                  title={`Do the work and attack!`}
                />
              </>
            )}
          </div>
          <div className="role">{`⚔️`} Developers</div>
        </div>

        <div>
          <div
            className="hero"
            style={{
              whiteSpace: "pre-line",
              borderColor: heroes[1].color,
              "--hero-color": heroes[1].color,
            }}
          >
            <div>
              <div className="fullName">{heroes[1].fullName}</div>
              <img
                className="profile"
                src={heroes[1].path}
                alt={heroes[1].fullName}
              />
            </div>
            {heroDamage[1] <= 0 ? (
              <div className="dead-label">FIRED</div>
            ) : (
              <>
                <progress
                  className="heroLife"
                  value={heroDamage[1]}
                  max={heroLife[1]}
                  title={`${heroDamage[1]} / ${heroLife[1]}`}
                />
                <progress
                  className={`heroMana ${
                    heroScores[1] >= maxScore ? "clickable" : ""
                  }`}
                  value={heroScores[1]}
                  max={maxScore}
                  onClick={handleClickOps}
                  title={`Help them with mana!`}
                />
              </>
            )}
          </div>
          <div className="role">{`⛏️`} DevOps</div>
        </div>

        <div>
          <div
            className="hero"
            style={{
              whiteSpace: "pre-line",
              borderColor: heroes[2].color,
              "--hero-color": heroes[2].color,
            }}
          >
            <div>
              <div className="fullName">{heroes[2].fullName}</div>
              <img
                className="profile"
                src={heroes[2].path}
                alt={heroes[2].fullName}
              />
            </div>
            {heroDamage[2] <= 0 ? (
              <div className="dead-label">FIRED</div>
            ) : (
              <>
                <progress
                  className="heroLife"
                  value={heroDamage[2]}
                  max={heroLife[2]}
                  title={`${heroDamage[2]} / ${heroLife[2]}`}
                />
                <progress
                  className={`heroMana ${
                    heroScores[2] >= maxScore ? "clickable" : ""
                  }`}
                  value={heroScores[2]}
                  max={maxScore}
                  onClick={handleClickFin}
                  title={`Boost them with €`}
                />
              </>
            )}
          </div>
          <div className="role">{`💰`} Finance</div>
        </div>

        <div>
          <div
            className="hero"
            style={{
              whiteSpace: "pre-line",
              borderColor: heroes[3].color,
              "--hero-color": heroes[3].color,
            }}
          >
            <div>
              <div className="fullName">{heroes[3].fullName}</div>
              <img
                className="profile"
                src={heroes[3].path}
                alt={heroes[3].fullName}
              />
            </div>
            {heroDamage[3] <= 0 ? (
              <div className="dead-label">FIRED</div>
            ) : (
              <>
                <progress
                  className="heroLife"
                  value={heroDamage[3]}
                  max={heroLife[3]}
                  title={`${heroDamage[3]} / ${heroLife[3]}`}
                />
                <progress
                  className={`heroMana ${
                    heroScores[3] >= maxScore ? "clickable" : ""
                  }`}
                  value={heroScores[3]}
                  max={maxScore}
                  onClick={handleClickUx}
                  title={`Change Figma, why not?`}
                />
              </>
            )}
          </div>
          <div className="role">
            {`🧝🏻`}
            UX/UI
          </div>
        </div>

        <div>
          <div
            className="hero"
            style={{
              whiteSpace: "pre-line",
              borderColor: heroes[4].color,
              "--hero-color": heroes[4].color,
            }}
          >
            <div>
              <div className="fullName">{heroes[4].fullName}</div>
              <img
                className="profile"
                src={heroes[4].path}
                alt={heroes[4].fullName}
              />
            </div>
            {heroDamage[4] <= 0 ? (
              <div className="dead-label">FIRED</div>
            ) : (
              <>
                <progress
                  className="heroLife"
                  value={heroDamage[4]}
                  max={heroLife[4]}
                  title={`${heroDamage[4]} / ${heroLife[4]}`}
                />
                <progress
                  className={`heroMana ${
                    heroScores[4] >= maxScore ? "clickable" : ""
                  }`}
                  value={heroScores[4]}
                  max={maxScore}
                  onClick={handleClickPm}
                  title={`Give some time to your team!`}
                />
              </>
            )}
          </div>
          <div className="role">
            {`🧙`}
            PMs/TMs
          </div>
        </div>
      </div>

      {/* Heroes Wins */}
      {gameOver && enemyLife <= 0 && (
        <div className="game-over-overlay">
          <div className="game-over-popup">
            <button
              className="game-over-close"
              aria-label="Close"
              onClick={() => setGameOver(false)}
            >
              ×
            </button>
            <div>The project is completed</div>
            <button className="game-restart" onClick={restartGame}>
              Next one
            </button>
            <button className="game-restart" onClick={onRestartSetup}>
              Boring team
            </button>
          </div>
        </div>
      )}

      {/* Enemy Wins */}
      {gameOver && enemyLife > 0 && (
        <div className="game-over-overlay">
          <div className="game-over-popup">
            <button
              className="game-over-close"
              aria-label="Close"
              onClick={() => setGameOver(false)}
            >
              ×
            </button>
            <div>Your team was fired!</div>
            <button className="game-restart" onClick={restartGame}>
              Give them a chance
            </button>
            <button className="game-restart" onClick={onRestartSetup}>
              Hire new team
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
